import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { projects } from "../../lib/api";
import { hashId } from "../../lib/format";
import { ChevronDown, Folder, Settings } from "../shared/Icon";
import { cn } from "../../lib/cn";

interface Props {
  value: string;
  onChange: (id: string) => void;
}

const TILE_BACKGROUNDS = [
  "rgb(var(--accent-rgb) / 0.14)",
  "rgb(var(--accent-rgb) / 0.08)",
  "var(--status-success-soft)",
  "var(--status-paused-soft)",
  "var(--status-review-soft)",
  "var(--status-running-soft)",
  "var(--status-queued-soft)",
  "var(--status-error-soft)",
];

export function ProjectCombobox({ value, onChange }: Props) {
  const { data } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projects.list(),
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const items = data ?? [];
  const current = items.find((p) => p.id === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full h-10 px-3 inline-flex items-center gap-2.5",
          "bg-bg-inset rounded-md border border-transparent",
          "hover:border-border-default",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg-surface",
          "transition-colors duration-fast ease-out",
        )}
      >
        {current ? (
          <>
            <Tile project={current} />
            <span className="text-body text-text-primary truncate flex-1 text-left">
              {current.name}
            </span>
          </>
        ) : (
          <>
            <span className="h-6 w-6 rounded-sm bg-bg-surface inline-flex items-center justify-center text-text-quaternary">
              <Folder />
            </span>
            <span className="text-body text-text-tertiary flex-1 text-left">
              Select a project…
            </span>
          </>
        )}
        <ChevronDown className="text-text-tertiary" />
      </button>
      {open && (
        <div
          role="listbox"
          className={cn(
            "absolute z-30 left-0 right-0 mt-1 max-h-[260px] overflow-y-auto",
            "bg-bg-surface rounded-md border border-border-subtle shadow-md py-1",
          )}
        >
          {items.length === 0 && (
            <div className="px-3 py-2 text-small text-text-tertiary">
              No projects yet.
            </div>
          )}
          {items.map((p) => {
            const selected = p.id === value;
            return (
              <button
                key={p.id}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                }}
                className={cn(
                  "w-full text-left flex items-center gap-2.5 px-3 py-2",
                  "transition-colors duration-fast ease-out",
                  selected
                    ? "bg-accent-soft text-text-primary"
                    : "text-text-secondary hover:bg-bg-inset hover:text-text-primary",
                )}
              >
                <Tile project={p} />
                <div className="min-w-0 flex-1">
                  <div className="text-body text-text-primary truncate">
                    {p.name}
                  </div>
                  <div className="id-token mono">{p.id}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Tile({
  project,
}: {
  project: { id: string; name: string };
}) {
  const tint = TILE_BACKGROUNDS[hashId(project.id, TILE_BACKGROUNDS.length)];
  const isSelf =
    project.name.toLowerCase() === "maestro" ||
    project.name.toLowerCase().includes("self");
  const initial = project.name.slice(0, 1).toUpperCase();
  return (
    <span
      className="h-6 w-6 inline-flex items-center justify-center rounded-sm shrink-0 text-[11px] font-medium text-text-secondary"
      style={{ background: tint }}
    >
      {isSelf ? <Settings /> : initial}
    </span>
  );
}
